import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export interface EmpGlosaContrato {
  id: string;
  contrato_id: string;
  boletim_id: string | null;
  execucao_id: string | null;
  medicao_item_id: string | null;
  tipo: string;
  descricao: string;
  valor_glosa: number;
  fundamentacao: string | null;
  status: 'pendente' | 'aceita' | 'contestada' | 'resolvida';
  categoria_defeito: string | null;
  contestacao_motivo: string | null;
  contestacao_por: string | null;
  contestacao_em: string | null;
  resolvido_por: string | null;
  resolvido_em: string | null;
  resolucao_observacao: string | null;
  reaberta: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  // computed
  origem: 'quality_gate' | 'boletim' | 'manual';
}

export const GLOSA_STATUS_OPTIONS = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'aceita', label: 'Aceita' },
  { value: 'contestada', label: 'Contestada' },
  { value: 'resolvida', label: 'Resolvida' },
] as const;

export const GLOSA_TIPO_LABELS: Record<string, string> = {
  qualidade: 'Qualidade',
  quantidade: 'Quantidade',
  documentacao: 'Documentação',
  prazo: 'Prazo',
  seguranca: 'Segurança',
  outro: 'Outro',
};

export const CATEGORIA_DEFEITO_LABELS: Record<string, string> = {
  material: 'Material',
  execucao: 'Execução',
  projeto: 'Projeto',
  seguranca: 'Segurança',
  documentacao: 'Documentação',
  outro: 'Outro',
};

/** Busca todas as glosas de um contrato (pré-medição e vinculadas a boletins) */
export function useEmpGlosasContrato(contratoId: string) {
  return useQuery({
    queryKey: ['emp-glosas-contrato', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_glosas')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      return ((data || []) as any[]).map((g): EmpGlosaContrato => ({
        ...g,
        origem: g.execucao_id && !g.boletim_id
          ? 'quality_gate'
          : g.boletim_id
            ? 'boletim'
            : 'manual',
      }));
    },
    enabled: !!contratoId,
  });
}

/** KPIs rápidos de glosas para badge no header */
export function useEmpGlosasResumo(contratoId: string) {
  return useQuery({
    queryKey: ['emp-glosas-resumo', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_glosas')
        .select('id, status, valor_glosa')
        .eq('contrato_id', contratoId);
      if (error) throw error;

      const items = (data || []) as any[];
      const ativas = items.filter(g => g.status === 'pendente' || g.status === 'contestada');
      const totalGlosado = items
        .filter(g => g.status !== 'resolvida')
        .reduce((s, g) => s + (Number(g.valor_glosa) || 0), 0);

      return {
        total: items.length,
        ativas: ativas.length,
        totalGlosado,
        temGlosaAtiva: ativas.length > 0,
      };
    },
    enabled: !!contratoId,
  });
}

export function useEmpGlosasMutations(contratoId: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['emp-glosas-contrato', contratoId] });
    qc.invalidateQueries({ queryKey: ['emp-glosas-resumo', contratoId] });
  };

  /** Atualizar valor da glosa (gestor pode ajustar o valor estimado) */
  const atualizarValorGlosa = useMutation({
    mutationFn: async ({ id, valor_glosa, fundamentacao }: { id: string; valor_glosa: number; fundamentacao?: string }) => {
      const { error } = await supabase
        .from('emp_glosas')
        .update({
          valor_glosa,
          ...(fundamentacao ? { fundamentacao } : {}),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: 'Valor da glosa atualizado' }); },
    onError: (e: Error) => toast({ title: 'Erro', description: e.message, variant: 'destructive' }),
  });

  /** Aceitar glosa (confirma o estorno) */
  const aceitarGlosa = useMutation({
    mutationFn: async ({ id, observacao }: { id: string; observacao?: string }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_glosas')
        .update({
          status: 'aceita',
          resolvido_por: user.id,
          resolvido_em: new Date().toISOString(),
          resolucao_observacao: observacao || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: '✓ Glosa aceita' }); },
    onError: (e: Error) => toast({ title: 'Erro', description: e.message, variant: 'destructive' }),
  });

  /** Contestar glosa (contratada discorda) */
  const contestarGlosa = useMutation({
    mutationFn: async ({ id, motivo }: { id: string; motivo: string }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_glosas')
        .update({
          status: 'contestada',
          contestacao_motivo: motivo,
          contestacao_por: user.id,
          contestacao_em: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: 'Contestação registrada' }); },
    onError: (e: Error) => toast({ title: 'Erro', description: e.message, variant: 'destructive' }),
  });

  /** Resolver glosa após regularização / reapresentação */
  const resolverGlosa = useMutation({
    mutationFn: async ({ id, observacao }: { id: string; observacao: string }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_glosas')
        .update({
          status: 'resolvida',
          resolvido_por: user.id,
          resolvido_em: new Date().toISOString(),
          resolucao_observacao: observacao,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: '✓ Glosa resolvida — atividade pode ser reapresentada' }); },
    onError: (e: Error) => toast({ title: 'Erro', description: e.message, variant: 'destructive' }),
  });

  /** Criar glosa manual (pelo gestor/planejamento) */
  const criarGlosa = useMutation({
    mutationFn: async (input: {
      tipo: string;
      descricao: string;
      valor_glosa: number;
      fundamentacao?: string;
      boletim_id?: string;
      execucao_id?: string;
      medicao_item_id?: string;
      categoria_defeito?: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_glosas')
        .insert({
          contrato_id: contratoId,
          boletim_id: input.boletim_id || null,
          execucao_id: input.execucao_id || null,
          medicao_item_id: input.medicao_item_id || null,
          tipo: input.tipo,
          descricao: input.descricao,
          valor_glosa: input.valor_glosa,
          fundamentacao: input.fundamentacao || null,
          categoria_defeito: input.categoria_defeito || null,
          status: 'pendente',
          created_by: user.id,
        });
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: 'Glosa registrada' }); },
    onError: (e: Error) => toast({ title: 'Erro ao criar glosa', description: e.message, variant: 'destructive' }),
  });

  return { atualizarValorGlosa, aceitarGlosa, contestarGlosa, resolverGlosa, criarGlosa };
}
